import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useNode } from '../../contexts/NodeContext';
import { XMarkIcon, ExclamationTriangleIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { NavTree, useActiveModelSurfaces } from '@trinetra/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useTour } from '../../tours/TourProvider';
import { ALL_SITES_NAV, SITE_NAV } from '../../constants/navigation';

import trinetraLogoWhite from '../../assets/logos/trinetra-logo-white.png';
import trinetraLogoLight from '../../assets/logos/trinetra-logo-light.png';
import samikshaLogoWhite from '../../assets/logos/samiksha-logo-white.png';
import samikshaLogoLight from '../../assets/logos/samiksha-logo-light.png';
import samikshaBrainIcon from '../../assets/logos/samiksha-brain-icon.png';

const S = {
  bg: 'var(--sidebar-bg)',
  border: 'var(--sidebar-border)',
  text: 'var(--sidebar-text)',
  textMuted: 'var(--sidebar-text-muted)',
  hover: 'var(--sidebar-hover)',
  activeBg: 'var(--sidebar-active-bg)',
  activeBorder: 'var(--sidebar-active-border)',
  divider: 'var(--sidebar-border)',
};

interface SidebarProps {
  collapsed: boolean;
  onCollapseToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({ collapsed, onCollapseToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { theme } = useTheme();
  // `selectedNodeId === null` IS the All Sites scope — not "nothing picked yet". That is what
  // lets this component simply pick a tree instead of hiding half a menu. Keyed on the id
  // rather than the resolved node, so a cold load with a stored site doesn't flash the All
  // Sites menu while the node list is still in flight.
  const { selectedNode, selectedNodeId } = useNode();
  const isDark = theme === 'dark';

  const { hasPermission } = useAuth();
  const { openGettingStarted } = useTour();
  // Per-model report entries only appear for models the SELECTED site actually runs (see
  // NavNode.requiresModel) — the registry is per-node, so switching site refetches. Fails
  // open, so a tunnel hiccup can't hide the reports.
  const { hasModel } = useActiveModelSurfaces(selectedNode?.nodeId ?? null);

  // Every entry in the per-site tree is served through that site's tunnel, so an offline site
  // makes all of them dead links. They stay visible but inert — hiding them would make the menu
  // appear to lose features rather than showing the site is simply down. The Layout's guard
  // blocks the matching routes, so this is the signpost, not the enforcement.
  const nodeOffline = !!selectedNode && selectedNode.status !== 'online';

  const sidebarContent = (
    <div className="flex flex-col h-full" style={{ backgroundColor: S.bg }}>
      {/* Logo — the way home, which is what a product logo means everywhere else. Collapsing
          lives on the rail's own chevron button (and only there): a logo that folded the
          navigation was undiscoverable as a toggle AND a trap for the click that meant
          "take me to the dashboard". */}
      <div className={`relative flex items-center justify-center flex-shrink-0 ${collapsed ? 'px-2 py-0 h-[56px]' : 'px-3 py-0'}`}>
        {collapsed ? (
          <Link
            to="/"
            onClick={() => { if (mobileOpen) onMobileClose(); }}
            title="Dashboard"
            aria-label="Dashboard"
            className="flex items-center justify-center"
          >
            <img
              src={isDark ? trinetraLogoWhite : trinetraLogoLight}
              alt="Trinetra"
              className="h-10 w-auto object-contain"
              draggable={false}
            />
          </Link>
        ) : (
          <div className="flex items-center justify-between w-full">
            <Link
              to="/"
              onClick={() => { if (mobileOpen) onMobileClose(); }}
              title="Dashboard"
              aria-label="Dashboard"
              className="mx-auto flex items-center justify-center"
            >
              <img
                src={isDark ? trinetraLogoWhite : trinetraLogoLight}
                alt="Trinetra"
                className="h-16 w-auto object-contain"
                draggable={false}
              />
            </Link>
            <button
              onClick={(e) => { e.stopPropagation(); onMobileClose(); }}
              className="p-1.5 rounded-lg transition-colors lg:hidden"
              style={{ color: S.textMuted }}
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="mx-3 flex-shrink-0" style={{ borderTop: `1px solid ${S.divider}` }} />

      {/* Navigation — ONE scope's tree. Which one is decided by the title bar's SiteSwitcher,
          so there is no selector here, no section labels, and nothing to tell apart: every row
          on screen belongs to the scope named above the page. */}
      <nav data-tour="sidebar" className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5 relative" style={{ scrollbarWidth: 'thin' }}>
        {selectedNodeId ? (
          <>
            {/* The one warning that has to stay in the sidebar: the switcher shows the site is
                down, but it doesn't explain why these particular rows stopped working. */}
            {nodeOffline && selectedNode && !collapsed && (
              <div
                className="mx-1 mb-2 flex items-start gap-2 rounded-lg px-2.5 py-2 text-[11px] leading-snug"
                style={{ color: 'var(--destructive)', backgroundColor: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)' }}
                role="status"
              >
                <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-[1px]" />
                <span>
                  <span className="font-semibold">Site offline.</span> These pages are unavailable
                  until {selectedNode.name} reconnects — System Health still works.
                </span>
              </div>
            )}
            {nodeOffline && selectedNode && collapsed && (
              <div
                className="mx-2 mb-2 flex justify-center rounded-lg py-1.5"
                style={{ color: 'var(--destructive)', backgroundColor: 'rgba(239,68,68,0.10)' }}
                title={`${selectedNode.name} is offline — these pages are unavailable`}
                role="status"
              >
                <ExclamationTriangleIcon className="h-4 w-4" />
              </div>
            )}

            <NavTree
              items={SITE_NAV}
              collapsed={collapsed}
              hasPermission={hasPermission}
              hasModel={hasModel}
              storageKey="trinetra.cloud.navOpenGroups.site"
              onNavigate={() => { if (mobileOpen) onMobileClose(); }}
              disabled={nodeOffline}
              disabledReason={nodeOffline ? `${selectedNode.name} is offline` : undefined}
            />
          </>
        ) : (
          <NavTree
            items={ALL_SITES_NAV}
            collapsed={collapsed}
            hasPermission={hasPermission}
            storageKey="trinetra.cloud.navOpenGroups.allSites"
            onNavigate={() => { if (mobileOpen) onMobileClose(); }}
          />
        )}
      </nav>

      {/* Guided tour — reopens the Getting started checklist. */}
      <div className={`flex-shrink-0 ${collapsed ? 'px-2' : 'px-3'} pb-2`}>
        <button
          onClick={() => { if (mobileOpen) onMobileClose(); openGettingStarted(); }}
          title="Guided tour"
          aria-label="Guided tour"
          className={`flex w-full items-center gap-2 rounded-lg border py-2 text-[13px] font-medium transition-colors ${collapsed ? 'justify-center px-0' : 'justify-center px-3'}`}
          style={{ color: S.text, borderColor: S.border }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = S.hover; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
        >
          <AcademicCapIcon className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>Guided tour</span>}
        </button>
      </div>

      {/* Divider */}
      <div className="mx-3 flex-shrink-0" style={{ borderTop: `1px solid ${S.divider}` }} />

      {/* Powered-by footer */}
      <div className={`flex-shrink-0 flex items-center justify-center ${collapsed ? 'px-2 py-0' : 'px-3 py-0'}`}>
        {!collapsed ? (
          <img src={isDark ? samikshaLogoWhite : samikshaLogoLight} alt="Samiksha.ai" className="h-14 w-auto object-contain" draggable={false} />
        ) : (
          <img src={samikshaBrainIcon} alt="Samiksha.ai" className="h-8 w-8 object-contain" draggable={false} />
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div
        className="hidden lg:flex flex-col h-screen flex-shrink-0 overflow-visible sidebar-transition relative"
        style={{
          width: collapsed ? 60 : 220,
          backgroundColor: S.bg,
          borderRight: `1px solid ${S.border}`,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {sidebarContent}
        {/* z-[41]: over the sticky header (z-40) it overlaps, but UNDER the alert board's
            fullscreen frame (z-[45]) and modals (z-50) — at z-50 it floated over both. */}
        <button
          onClick={onCollapseToggle}
          className="absolute -right-3.5 top-12 hidden lg:flex w-7 h-7 rounded-full bg-white border-2 border-[#6B8FA5]/20 hover:bg-[#E8B849]/10 hover:border-[#E8B849]/50 text-[#2E4A5B] shadow-md transition-all duration-200 items-center justify-center z-[41]"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden" onClick={onMobileClose} />
          <div
            className="fixed top-0 left-0 bottom-0 z-50 w-[240px] flex flex-col lg:hidden overflow-hidden"
            style={{ backgroundColor: S.bg, borderRight: `1px solid ${S.border}` }}
          >
            {sidebarContent}
          </div>
        </>
      )}
    </>
  );
}
